/**
 * @license
 * Copyright (c) 2017, Barclays Africa Technology, All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file
 */

import {Compiler, Injectable, NgModuleFactory, Type, NgModuleRef, Injector} from '@angular/core';

class ModuleLoaderPromiseCacheEntry {
    promise: Promise<NgModuleFactory<any>>;
    resolve: (value: NgModuleFactory<any>) => void;
    reject: (error: string) => void;
}

/**
 * Service to load modules on demand and compile them
 */
@Injectable()
export class ModuleLoaderService {

    private _moduleCache = new Map<string, NgModuleFactory<any>>();
    private _moduleRefCache = new Map<string, NgModuleRef<any>>();
    private _promiseCache = new Map<string, ModuleLoaderPromiseCacheEntry>();
    // private _traceEnabled = process.env.config.isDevelopment;
    private _traceEnabled = false;

    /**
     * Create the instance of ModuleLoaderService
     * @param compiler The instance of default compiler available on runtime
     */
    constructor(private compiler: Compiler, private injector: Injector) {}

    /**
     * Load module and compile it
     * @param url The full url to the module in format:
     *   http://path_to_the_js_file#ModuleName
     */
    load(url: string): Promise<NgModuleFactory<any>> {
        let [path, moduleName] = url.split('#');

        if (!moduleName) {
            throw new Error(`[ModuleLoaderService]: Please specify the Module's Name after # in "${url}"`);
        }

        if (this._traceEnabled) console.log(`[ModuleLoaderService]: Requesting ${url}`);

        // Try to find the url in the cache of loaded and compiled modules
        if (this._moduleCache.has(url)) {
            // Module was loaded before and compiled - let's use it
            if (this._traceEnabled) console.log(`[ModuleLoaderService]: Returning ${url} from module cache...`);
            return Promise.resolve(this._moduleCache.get(url));
        }

        // Try to find the url in the cashe of loading scripts        
        if (this._promiseCache.has(url)) {
            if (this._traceEnabled) console.log(`[ModuleLoaderService]: Returning ${path} from promise cache...`);
            return this._promiseCache.get(url).promise;
        }

        // Let's create the ModuleLoaderPromiseCacheEntry instance for loading module
        this._promiseCache.set(url, new ModuleLoaderPromiseCacheEntry());

        let promise: Promise<NgModuleFactory<any>> = new Promise((resolve, reject) => {
            let cachedPromise: ModuleLoaderPromiseCacheEntry = this._promiseCache.get(url);
            cachedPromise.resolve = <any>resolve;
            cachedPromise.reject = reject;

            cachedPromise.promise = <Promise<NgModuleFactory<any>>>this.loadScript(path, moduleName)
                .catch(() => {                                        
                    if (process.env.config.application === 'threshold') {
                        let moduleType = (moduleName.indexOf('Widget') >= 0 ? 'widget' : 'connected-experience'); 
                        console.error(`Error occurred loading ${moduleType}:`, moduleName);
                    }
                })
                .then((type: Type<{}>) => this.checkNotEmpty(type, path, moduleName))
                .then((type: Type<{}>) => {
                    if (this._traceEnabled) console.log(`[ModuleLoaderService]: Script loaded from ${path}...`);

                    this._promiseCache.delete(url);
                    // Reserve the room for compiled module
                    this._moduleCache.set(url, <any>{});

                    this.compiler.compileModuleAsync(type).then((factory: NgModuleFactory<{}>) => {
                        if (this._traceEnabled) console.log(`[ModuleLoaderService]: Module compiled from ${path}...`);
                        this._moduleCache.set(url, factory);
                        if (this._traceEnabled) console.log(`[ModuleLoaderService]: Module ${moduleName} from ${path} saved in the module cache`);
                        // Delete script from global space
                        // delete global[moduleName];
                        resolve(factory);
                    });
                    return this._moduleCache.get(url);
                }, reject);
        });

        this._promiseCache.get(url).promise = promise;

        return promise;
    }

    /**
     * Load and store compiled module
     * @param url 
     */
    loadModuleRef(url: string): Promise<NgModuleRef<any>> {

        return this.load(url)
            .catch(() => {                                        

            })
            .then((moduleFactory: NgModuleFactory<any>) => {
                if (this._moduleRefCache.has(url)) {
                    if (this._traceEnabled) console.log(`[WidgetComponent]: Returning compiled from module cache...`);
                    return this._moduleRefCache.get(url); 
                }
                else {
                    let ngModuleRef = moduleFactory.create(this.injector);
                    this._moduleRefCache.set(url, ngModuleRef);
                    return ngModuleRef;
                }     
        });
    }

    /**
     * 
     * @param value 
     * @param modulePath 
     * @param moduleName 
     */
    private checkNotEmpty(value: any, modulePath: string, moduleName: string): any {
        if (!value) {
            throw new Error(`[ModuleLoaderService]: Cannot find '${moduleName}' in '${modulePath}'`);
        }
        return value;
    }

    /**
     * 
     * @param path 
     * @param name 
     */
    private loadScript(path: string, name: string): Promise<any> {
        let promise: Promise<any> = new Promise((resolve, reject) => {
            let script = document.createElement('script');
            script.type = 'text/javascript';
            script.charset = 'utf-8';
            script.async = true;

            script.addEventListener('load', load, false);
            script.addEventListener('error', error, false);

            function load() {
                resolve();
                cleanup();
            }

            // note this does not catch execution errors
            function error(err) {
                cleanup();
                reject(new Error('Fetching ' + path));
            }

            function cleanup() {
                script.removeEventListener('load', load, false);
                script.removeEventListener('error', error, false);
                document.head.removeChild(script);
            }

            script.src = path;
            let host: string = '';
            if(global['__ENV_environmentApi']) {
                host = global['__ENV_environmentApi'];
            }
            // only if nonprod or localhost in the url do the query string cache busting
            if (host.indexOf('nonprod') >= 0 || host.indexOf('localhost') >= 0) {
                script.src = path + '?' + (Math.random()) + '#' + name;
            }
            document.head.appendChild(script); // append to head
        });

        return promise.then(() => {
            if (global[name]) {
                if (this._traceEnabled) console.log(`[ModuleLoaderService]: The script loaded module ${name} from ${path}`);
                return global[name];
            } else {
                throw new Error(`"${name}" was not created by "${path}"`);
            }
        });
    }
}



// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./~/angular-router-loader/src!./src/app/celine/services/module-loader.service.ts